import React from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Navbar, Footer } from "../components/Layout";
import { EmailVerifyBanner } from "../components/EmailVerifyBanner";
import { Crown, Calendar, Mail, ArrowRight, Gift, Copy, Share2 } from "lucide-react";
import { useNoIndex } from "../hooks/useNoIndex";

const formatDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : "—";

const Account = () => {
  useNoIndex();
  const { user, loading, logout } = useAuth();
  if (loading) return <div className="washi-bg min-h-screen"><Navbar /></div>;
  if (!user) return <Navigate to="/login" replace />;

  const statusLabel = {
    trial: "Essai gratuit (3 jours)",
    active: "Premium actif",
    lifetime: "Premium à vie ✨",
    expired: "Essai expiré",
  }[user.premium_status] || "—";

  const expiry = user.premium_status === "lifetime" ? "Aucune expiration"
    : user.premium_status === "trial" ? formatDate(user.trial_ends_at)
    : user.premium_status === "active" ? formatDate(user.premium_until)
    : "Expiré";

  return (
    <div className="washi-bg min-h-screen" data-testid="account-page">
      <Navbar />
      <EmailVerifyBanner />
      <div className="max-w-3xl mx-auto px-6 sm:px-12 py-16">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#E94A3F] mb-3">Mon compte</div>
        <h1 className="font-display font-black text-5xl tracking-tighter leading-[1] text-[#1A2A40] mb-10">
          Bonjour, {user.name}.
        </h1>

        <div className="bg-[#1A2A40] text-white rounded-3xl p-8 sm:p-10 mb-6 shadow-brut border-2 border-[#1A2A40] relative overflow-hidden" data-testid="subscription-card">
          <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-[#E94A3F]/30 blur-3xl" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <Crown className="w-5 h-5 text-[#FFD166]" />
              <span className="text-xs font-bold uppercase tracking-widest text-[#FFD166]">Abonnement</span>
              {user.is_founder && (
                <span className="ml-auto bg-[#FFD166] text-[#1A2A40] rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest border-2 border-[#FFD166]" data-testid="founder-badge">
                  👑 Fondateur · 創立者
                </span>
              )}
            </div>
            <div className="font-display font-black text-3xl sm:text-4xl mb-1">{statusLabel}</div>
            <div className="text-white/70 mb-6 text-sm inline-flex items-center gap-2">
              <Calendar className="w-4 h-4" /> {user.premium_status === "lifetime" ? expiry : `Jusqu'au ${expiry}`}
            </div>
            {user.plan && (
              <div className="text-sm text-white/70">Plan : <strong className="text-white capitalize">{user.plan}</strong></div>
            )}
            {!user.is_premium && (
              <p className="text-white/80 text-sm mt-3">
                Votre essai est terminé. Choisissez un plan pour reprendre l'aventure.
              </p>
            )}
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/pricing" data-testid="upgrade-btn" className="bg-[#E94A3F] text-white font-bold py-3 px-5 rounded-xl shadow-brut-sm hover-lift inline-flex items-center gap-2">
                {user.is_premium && user.premium_status !== "lifetime" ? "Prolonger / Changer de plan" : "Choisir un plan"} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mb-6">
          <InfoCard icon={<Mail />} label="Email" value={user.email} />
          <InfoCard icon={<Calendar />} label="Membre depuis" value={formatDate(user.created_at)} />
        </div>

        <div className="bg-[#FDFBF7] rounded-3xl p-6 border-2 border-[#1A2A40]/10">
          <div className="text-xs font-bold uppercase tracking-widest text-[#E94A3F] mb-1">Statistiques</div>
          <div className="grid grid-cols-3 gap-4 mt-3">
            <Stat k={`${user.xp}`} v="XP" />
            <Stat k={`${user.streak} j`} v="Série" />
            <Stat k={`${user.completed_lessons?.length || 0}`} v="Leçons" />
          </div>
        </div>

        <ReferralCard />

        <div className="mt-10 text-center">
          <button onClick={logout} data-testid="account-logout-btn" className="text-sm text-[#4A5568] hover:text-[#E94A3F] underline">
            Se déconnecter
          </button>
        </div>
      </div>
      <Footer />
    </div>
  );
};

const InfoCard = ({ icon, label, value }) => (
  <div className="bg-[#FDFBF7] rounded-2xl p-5 border-2 border-[#1A2A40]/10 flex items-center gap-4">
    <div className="w-10 h-10 rounded-xl bg-[#F3F6F1] flex items-center justify-center text-[#E94A3F]">{icon}</div>
    <div className="min-w-0">
      <div className="text-xs uppercase tracking-wider text-[#A0AEC0]">{label}</div>
      <div className="font-bold text-[#1A2A40] truncate">{value}</div>
    </div>
  </div>
);

const Stat = ({ k, v }) => (
  <div>
    <div className="font-display font-black text-2xl text-[#E94A3F]">{k}</div>
    <div className="text-xs uppercase tracking-wider text-[#4A5568]">{v}</div>
  </div>
);

const ReferralCard = () => {
  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    api.get("/referral/my-code").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const copy = (text, label) => {
    navigator.clipboard.writeText(text).then(() => toast.success(`${label} copié !`));
  };
  const share = () => {
    if (!data) return;
    const text = `J'apprends le japonais avec Japonais Pas à Pas : méthode complète + tuteur IA. Avec mon code, tu as -10€ : ${data.share_url}`;
    if (navigator.share) {
      navigator.share({ title: "Japonais Pas à Pas", text, url: data.share_url }).catch(() => {});
    } else {
      copy(text, "Lien de partage");
    }
  };

  if (!data) return null;
  return (
    <div className="mt-6 bg-gradient-to-br from-[#FFF5E0] to-[#FFF5F0] rounded-3xl p-6 sm:p-8 border-2 border-[#FFD166]" data-testid="referral-card">
      <div className="flex items-center gap-2 mb-3">
        <Gift className="w-5 h-5 text-[#E94A3F]" />
        <div className="text-xs font-bold uppercase tracking-widest text-[#E94A3F]">Parrainage</div>
      </div>
      <h3 className="font-display font-black text-2xl sm:text-3xl text-[#1A2A40] mb-2 leading-tight">
        Invite un ami, gagne <span className="text-[#E94A3F]">1 mois gratuit</span>
      </h3>
      <p className="text-sm text-[#4A5568] mb-5 leading-relaxed">
        Partage ton code : ton ami <strong>-10 €</strong> sur son premier mois,
        toi <strong>+1 mois offert</strong> dès qu'il s'abonne.
      </p>

      <div className="grid sm:grid-cols-2 gap-3 mb-5">
        <div className="bg-white rounded-xl p-4 border border-[#1A2A40]/10">
          <div className="text-[10px] uppercase tracking-widest text-[#A0AEC0] mb-1">Ton code</div>
          <div className="flex items-center gap-2">
            <code className="font-mono font-bold text-xl text-[#E94A3F] tracking-widest flex-1" data-testid="referral-code">{data.code}</code>
            <button onClick={() => copy(data.code, "Code")} data-testid="copy-code-btn" className="p-2 rounded-lg bg-[#F9F6F0] hover:bg-[#FFD166] transition-colors">
              <Copy className="w-4 h-4 text-[#1A2A40]" />
            </button>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-[#1A2A40]/10">
          <div className="text-[10px] uppercase tracking-widest text-[#A0AEC0] mb-1">Lien à partager</div>
          <div className="flex items-center gap-2">
            <code className="font-mono text-xs text-[#1A2A40] truncate flex-1" data-testid="referral-url">{data.share_url}</code>
            <button onClick={() => copy(data.share_url, "Lien")} data-testid="copy-url-btn" className="p-2 rounded-lg bg-[#F9F6F0] hover:bg-[#FFD166] transition-colors">
              <Copy className="w-4 h-4 text-[#1A2A40]" />
            </button>
          </div>
        </div>
      </div>

      <button onClick={share} data-testid="share-btn" className="w-full sm:w-auto bg-[#E94A3F] text-white font-bold py-3 px-5 rounded-xl shadow-brut-sm hover-lift inline-flex items-center justify-center gap-2">
        <Share2 className="w-4 h-4" /> Partager
      </button>

      <div className="grid grid-cols-3 gap-3 mt-5 pt-5 border-t border-[#1A2A40]/10">
        <div className="text-center">
          <div className="font-display font-black text-2xl text-[#1A2A40]" data-testid="referral-validated">{data.validated_count}</div>
          <div className="text-[10px] uppercase tracking-wider text-[#4A5568]">Amis abonnés</div>
        </div>
        <div className="text-center">
          <div className="font-display font-black text-2xl text-[#1A2A40]" data-testid="referral-pending">{data.pending_count}</div>
          <div className="text-[10px] uppercase tracking-wider text-[#4A5568]">En attente</div>
        </div>
        <div className="text-center">
          <div className="font-display font-black text-2xl text-[#34D399]" data-testid="referral-months">{data.months_earned}</div>
          <div className="text-[10px] uppercase tracking-wider text-[#4A5568]">Mois gagnés</div>
        </div>
      </div>
    </div>
  );
};

export default Account;
